import React, { useMemo } from 'react';
import WorldMap from '../../components/WorldMap';
import ContextMenu from '../../components/ContextMenu';
import { type LiveVehicleTrack } from '../../components/WorldMap';
import {
  getInterventionDisplayLines,
  getInterventionPhaseUiKey,
  normalizeInterventionInstanceStatut,
  type InstanceRow,
} from '../../components/interventions/instanceModel';
import { formatDistanceKm } from '../mapUtils';
import { getBuildingLatLng } from '../mapShared';
import { Batiment, Vehicule } from '../../../../shared/types/game';
import { type ContextMenuState } from '../hooks/useMapData';
import RadioJournalDispatch from '../../components/interventions/RadioJournalDispatch';

interface DispatchViewProps {
  activeInterventions: InstanceRow[];
  selectedIntervention: InstanceRow | null;
  selectedDisplay: { headline: string; subline: string } | null;
  setSelectedInterventionId: (id: number) => void;
  getInterventionPriority: (inv: InstanceRow) => number;
  setInterventionPriority: (inv: InstanceRow, priority: number) => void;
  setMainView: (view: 'dispatch' | 'resources' | 'buildings' | 'interventions') => void;
  buildings: Batiment[];
  interventions: InstanceRow[];
  liveVehicleTracks: LiveVehicleTrack[];
  showInterventionSectors: boolean;
  contextMenu: ContextMenuState;
  setContextMenu: (state: ContextMenuState) => void;
  handleBuildingPlace: (lat: number, lng: number, buildingType: string, buildingName: string) => void;
  availableVehicles: number;
  availablePersonnel: number;
  availabilityPercent: number;
  availabilityCircumference: number;
  availabilityOffset: number;
  handleFutureFeature: (label: string) => void;
  availableUnitsRows: {
    id: number;
    unit: string;
    type: string;
    service: string;
    crew: string;
    state: string;
    eta: string;
  }[];
  buildingFilterOptions: { id: number; name: string }[];
  vehicleTypeFilterOptions: string[];
  playerVehicles: Vehicule[];
  unitsSearch: string;
  setUnitsSearch: (v: string) => void;
  buildingFilter: number | 'all';
  setBuildingFilter: (v: number | 'all') => void;
  vehicleTypeFilter: string;
  setVehicleTypeFilter: (v: string) => void;
  unitsFilter: 'all' | 'disponible' | 'en_mission' | 'en_maintenance';
  setUnitsFilter: (v: 'all' | 'disponible' | 'en_mission' | 'en_maintenance') => void;
  selectedInterventionVehicleRows: Vehicule[];
}

const DispatchView: React.FC<DispatchViewProps> = ({
  activeInterventions,
  selectedIntervention,
  selectedDisplay,
  setSelectedInterventionId,
  getInterventionPriority,
  setInterventionPriority,
  setMainView,
  buildings,
  interventions,
  liveVehicleTracks,
  showInterventionSectors,
  contextMenu,
  setContextMenu,
  handleBuildingPlace,
  availableVehicles,
  availablePersonnel,
  availabilityPercent,
  availabilityCircumference,
  availabilityOffset,
  handleFutureFeature,
  availableUnitsRows: _availableUnitsRows,
  buildingFilterOptions: _buildingFilterOptions,
  vehicleTypeFilterOptions: _vehicleTypeFilterOptions,
  playerVehicles: _playerVehicles,
  unitsSearch: _unitsSearch,
  setUnitsSearch: _setUnitsSearch,
  buildingFilter: _buildingFilter,
  setBuildingFilter: _setBuildingFilter,
  vehicleTypeFilter: _vehicleTypeFilter,
  setVehicleTypeFilter: _setVehicleTypeFilter,
  unitsFilter: _unitsFilter,
  setUnitsFilter: _setUnitsFilter,
  selectedInterventionVehicleRows: _selectedInterventionVehicleRows,
}) => {
  /** Alertes calculées en temps réel depuis les données disponibles */
  // Centre de référence = moyenne des positions des bâtiments (parsées depuis localisation).
  // Sert à estimer la distance aux interventions. Hoisté hors de la liste pour éviter le recalcul par item.
  const [refLat, refLng] = useMemo<[number, number]>(() => {
    if (buildings.length === 0) return [45.764, 4.8357];
    const sum = buildings.reduce(
      (acc, b) => {
        const { lat, lng } = getBuildingLatLng(b);
        return { lat: acc.lat + lat, lng: acc.lng + lng };
      },
      { lat: 0, lng: 0 }
    );
    return [sum.lat / buildings.length, sum.lng / buildings.length];
  }, [buildings]);

  const alerts = useMemo(() => {
    type AlertLevel = 'critical' | 'warning' | 'info';
    const result: { level: AlertLevel; text: string }[] = [];

    // Interventions en attente sans engagement
    const waiting = activeInterventions.filter(
      (i) => normalizeInterventionInstanceStatut(i.statut || '') === 'EN_ATTENTE'
    );
    if (waiting.length > 0) {
      result.push({
        level: 'critical',
        text: `${waiting.length} intervention${waiting.length > 1 ? 's' : ''} en attente de prise en charge`,
      });
    }

    // Véhicules disponibles faibles
    if (availableVehicles === 0) {
      result.push({ level: 'critical', text: 'Aucun véhicule disponible — flotte saturée' });
    } else if (availableVehicles <= 2) {
      result.push({ level: 'warning', text: `Seulement ${availableVehicles} véhicule${availableVehicles > 1 ? 's' : ''} disponible${availableVehicles > 1 ? 's' : ''}` });
    }

    // Personnel disponible faible
    if (availablePersonnel === 0) {
      result.push({ level: 'warning', text: 'Aucun agent disponible' });
    } else if (availablePersonnel <= 3) {
      result.push({ level: 'warning', text: `Effectif faible : ${availablePersonnel} agent${availablePersonnel > 1 ? 's' : ''} disponible${availablePersonnel > 1 ? 's' : ''}` });
    }

    // Interventions actives en cours
    const active = activeInterventions.filter(
      (i) => normalizeInterventionInstanceStatut(i.statut || '') !== 'EN_ATTENTE'
    );
    if (active.length > 0) {
      result.push({ level: 'info', text: `${active.length} intervention${active.length > 1 ? 's' : ''} en cours de traitement` });
    }

    // Aucune intervention = calme opérationnel
    if (activeInterventions.length === 0) {
      result.push({ level: 'info', text: 'Aucune intervention active — calme opérationnel' });
    }

    return result;
  }, [activeInterventions, availableVehicles, availablePersonnel]);

  const alertStyle: Record<string, string> = {
    critical: 'border-red-500/40 bg-red-950/35 text-red-200',
    warning:  'border-amber-500/35 bg-amber-950/25 text-amber-200',
    info:     'border-sky-500/25 bg-sky-950/20 text-sky-300',
  };

  return (
  <div className="grid h-full grid-cols-[360px,minmax(0,1fr),380px] gap-3">
    <aside className="flex min-h-0 flex-col gap-3">
      <section className="min-h-0 flex-1 rounded-lg border border-slate-700/75 bg-slate-900/90 shadow-xl shadow-black/25 flex flex-col">
        <div className="shrink-0 border-b border-slate-700/70 px-3 py-2 flex items-center justify-between">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-red-300">
            Interventions en cours ({activeInterventions.length})
          </h2>
          {activeInterventions.length > 0 && (
            <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-bold text-red-300 border border-red-500/30">
              {activeInterventions.length}
            </span>
          )}
        </div>
        <div className="flex-1 min-h-0 space-y-2 overflow-y-auto p-2">
          {activeInterventions.map((inv) => {
            const lines = getInterventionDisplayLines(inv, 'Intervention');
            const priority = getInterventionPriority(inv);
            return (
              <button
                key={inv.id}
                type="button"
                onClick={() => setSelectedInterventionId(inv.id)}
                className={`w-full rounded-md border p-2 text-left transition-colors ${
                  selectedIntervention?.id === inv.id
                    ? 'border-red-500/70 bg-red-950/35'
                    : 'border-slate-700/80 bg-slate-950/70 hover:bg-slate-800/80'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-xs font-bold uppercase text-slate-100">{lines.headline}</p>
                    <p className="mt-0.5 line-clamp-1 text-[10px] text-slate-400">{lines.subline || 'Adresse non précisée'}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="rounded border border-red-500/45 bg-red-500/20 px-1.5 py-0.5 text-[9px] font-semibold text-red-200">
                      Priorité
                    </span>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={priority}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setInterventionPriority(inv, Number(e.target.value))}
                      className="w-14 rounded border border-red-500/45 bg-slate-900/95 px-1.5 py-0.5 text-[10px] font-semibold text-red-200 outline-none focus:border-red-400"
                      title="Priorité modifiable par le joueur"
                    />
                  </div>
                </div>
                <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-400">
                  <span>#{inv.numero_intervention || inv.id}</span>
                  <span>{formatDistanceKm(refLat, refLng, Number(inv.latitude || refLat), Number(inv.longitude || refLng))}</span>
                </div>
              </button>
            );
          })}
          {activeInterventions.length === 0 ? (
            <p className="rounded-md border border-slate-700/80 bg-slate-950/70 px-2 py-6 text-center text-xs text-slate-500">
              Aucune intervention active
            </p>
          ) : null}
        </div>
      </section>

      <section className="shrink-0 rounded-lg border border-slate-700/75 bg-slate-900/90 shadow-xl shadow-black/25">
        <div className="border-b border-slate-700/70 px-3 py-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-200">
            {selectedDisplay?.headline || 'Sélectionner une intervention'}
          </h3>
        </div>
        {selectedIntervention ? (
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 px-3 py-2 text-[11px]">
            <div>
              <p className="text-slate-500 text-[10px] uppercase tracking-wide">Adresse</p>
              <p className="text-slate-100 truncate">{selectedDisplay?.subline || '—'}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px] uppercase tracking-wide">Appel reçu</p>
              <p className="text-slate-100">
                {selectedIntervention.date_creation
                  ? new Date(selectedIntervention.date_creation).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                  : '—'}
              </p>
            </div>
            <div className="col-span-2 mt-1">
              <button
                type="button"
                onClick={() => setMainView('interventions')}
                className="w-full rounded border border-sky-500/45 bg-sky-500/10 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-sky-200 hover:bg-sky-500/20"
              >
                Gérer cette intervention →
              </button>
            </div>
          </div>
        ) : (
          <p className="px-3 py-2 text-[11px] text-slate-500">Cliquez sur une intervention pour voir ses détails.</p>
        )}
      </section>
    </aside>

    <main className="grid min-h-0 grid-rows-[minmax(0,1fr),280px] gap-3">
      <section className="relative min-h-0 rounded-lg border border-slate-700/75 bg-slate-900 shadow-xl shadow-black/30">
        <WorldMap
          buildings={buildings}
          showInterventionSectors={showInterventionSectors}
          interventions={interventions}
          liveVehicles={liveVehicleTracks}
        />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-slate-950/55 to-transparent" />

        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={() => setContextMenu(null)}
            onBuildClick={(buildingType, buildingName) =>
              handleBuildingPlace(contextMenu.lat, contextMenu.lng, buildingType, buildingName)
            }
          />
        )}
      </section>

      <section className="grid grid-cols-[1.25fr,1fr,1fr] gap-3 text-[11px]">
        <div className="rounded-lg border border-slate-700/85 bg-slate-900 p-3.5 shadow-xl shadow-black/35 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.12em] text-red-300">Alertes et notifications</p>
            {alerts.filter(a => a.level === 'critical').length > 0 && (
              <span className="rounded-full bg-red-500/20 border border-red-500/40 px-1.5 py-0.5 text-[9px] font-bold text-red-300">
                {alerts.filter(a => a.level === 'critical').length}
              </span>
            )}
          </div>
          <div className="space-y-1">
            {alerts.map((alert, i) => (
              <div key={i} className={`rounded border px-2 py-1 text-[10px] leading-snug ${alertStyle[alert.level]}`}>
                {alert.text}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setMainView('interventions')}
            className="mt-auto w-full rounded border border-slate-600/70 bg-slate-800/80 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-200 hover:bg-slate-700/80 transition-colors"
          >
            Gérer les interventions →
          </button>
        </div>
        <div className="rounded-lg border border-slate-700/85 bg-slate-900 p-3.5 shadow-xl shadow-black/35">
          <p className="text-[10px] uppercase tracking-[0.12em] text-emerald-300">Ressources globales</p>
          <div className="mt-2 space-y-2 text-slate-300">
            <div className="rounded border border-slate-700/70 bg-slate-950/80 px-2.5 py-1.5">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Flotte véhicules</p>
              <p className="mt-0.5 text-sm font-semibold text-slate-100">
                {availableVehicles} <span className="text-xs font-normal text-emerald-300">dispo.</span>
              </p>
            </div>
            <div className="rounded border border-slate-700/70 bg-slate-950/80 px-2.5 py-1.5">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Effectif personnel</p>
              <p className="mt-0.5 text-sm font-semibold text-slate-100">
                {availablePersonnel} <span className="text-xs font-normal text-emerald-300">actifs</span>
              </p>
            </div>
          </div>
          <div className="mt-2.5 rounded border border-slate-700/70 bg-slate-950/75 p-2">
            <div className="mb-1 flex items-center justify-between text-[10px] text-slate-500">
              <span>Taux de disponibilité</span>
              <span>{availabilityPercent}%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative h-16 w-16 shrink-0">
                <svg viewBox="0 0 80 80" className="h-16 w-16 -rotate-90">
                  <circle cx="40" cy="40" r="30" stroke="rgba(51,65,85,0.9)" strokeWidth="8" fill="none" />
                  <circle
                    cx="40"
                    cy="40"
                    r="30"
                    stroke="rgba(16,185,129,0.9)"
                    strokeWidth="8"
                    strokeLinecap="round"
                    fill="none"
                    strokeDasharray={availabilityCircumference}
                    strokeDashoffset={availabilityOffset}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-emerald-300">
                  {availabilityPercent}%
                </div>
              </div>
              <div className="min-w-0 text-[10px] text-slate-400">
                <p>Disponibilité opérationnelle</p>
                <p className="mt-0.5 text-emerald-300">Capacité de réponse conforme</p>
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-slate-700/85 bg-slate-900 p-3.5 shadow-xl shadow-black/35">
          <p className="text-[10px] uppercase tracking-[0.12em] text-sky-300">Alliances actives</p>
          <div className="mt-2 rounded border border-slate-700/70 bg-slate-950/75 px-2 py-2 text-slate-300">
            En cours de mise à jour, disponible prochainement.
          </div>
          <button
            type="button"
            onClick={() => handleFutureFeature('Alliances')}
            className="mt-2 w-full rounded border border-slate-700/70 bg-slate-800/50 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400"
          >
            Voir toutes les alliances
          </button>
        </div>
      </section>
    </main>

    <aside className="flex min-h-0 flex-col gap-3">
      <section className="min-h-0 flex-1 rounded-lg border border-slate-700/75 bg-slate-900/90 shadow-xl shadow-black/25 flex flex-col">
        <div className="shrink-0 border-b border-slate-700/70 px-3 py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {/* Icône radio */}
            <svg className="w-3.5 h-3.5 text-amber-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
            </svg>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-300/90">
              Journal radio
            </h3>
          </div>
          {selectedIntervention && (
            <span className="text-[10px] text-slate-500 font-mono truncate max-w-[120px]">
              #{selectedIntervention.numero_intervention || selectedIntervention.id}
            </span>
          )}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {selectedIntervention ? (
            <RadioJournalDispatch instanceId={selectedIntervention.id} />
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-8 px-4 text-center">
              <svg className="w-8 h-8 text-slate-700 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
              </svg>
              <p className="text-[11px] text-slate-600">Sélectionnez une intervention<br/>pour voir son journal radio</p>
            </div>
          )}
        </div>
      </section>
    </aside>
  </div>
  );
};

export default DispatchView;
